/**
 * EventSystem — Random travel event triggering and resolution.
 * Fires narrative events as the party moves through the world.
 * All event definitions are JSON-driven from random_events.json.
 *
 * @module EventSystem
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import RANDOM_EVENTS_DATA from '../data/random_events.json';
import LootSystem from './LootSystem.js';
import { createRNG, globalRNG } from '../utils/RNG.js';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Base probability of a random event triggering per tile move. */
const BASE_TRIGGER_CHANCE = 0.15;

// ─── EventSystem ──────────────────────────────────────────────────────────────

const EventSystem = {
  /** @type {Map<string, object>} Event definitions keyed by id */
  _events: new Map(),

  /** @type {boolean} */
  _ready: false,

  /** @type {string|null} ID of the currently active event (if any) */
  _activeEventId: null,

  // ── Init ──────────────────────────────────────────────────────────────────

  /**
   * Load event definitions from random_events.json. Call once at startup.
   */
  init() {
    this._events = new Map();
    for (const evt of RANDOM_EVENTS_DATA) {
      this._events.set(evt.id, evt);
    }
    this._ready = true;
    DebugLog.info(`[encounter] EventSystem initialized; events=${this._events.size}`);
  },

  // ── Trigger check ─────────────────────────────────────────────────────────

  /**
   * Determine whether a random event should trigger on this tile move.
   * Uses 15% base chance, scaled slightly by region level.
   *
   * @param {object} partyState — Expects { currentRegionLevel?: number }
   * @param {ReturnType<import('../utils/RNG.js').createRNG>} [rng]
   * @returns {boolean}
   */
  shouldTrigger(partyState = {}, rng = null) {
    const r = rng ?? globalRNG;
    const regionLevel = partyState.currentRegionLevel ?? 1;
    // Slight scaling: +1% per region level above 1, capped at 25%
    const chance = Math.min(BASE_TRIGGER_CHANCE + (regionLevel - 1) * 0.01, 0.25);
    const triggered = r.nextFloat() < chance;
    if (triggered) {
      DebugLog.info(`[encounter] Random event triggered; chance=${(chance * 100).toFixed(1)}%`);
    }
    return triggered;
  },

  // ── Event selection ───────────────────────────────────────────────────────

  /**
   * Pick an appropriate random event for the current biome and region level.
   * Filters by biome (if specified) and minLevel/maxLevel.
   *
   * @param {string} [biome='forest']
   * @param {number} [regionLevel=1]
   * @param {ReturnType<import('../utils/RNG.js').createRNG>} [rng]
   * @returns {object|null} Event definition or null if none available
   */
  getRandomEvent(biome = 'forest', regionLevel = 1, rng = null) {
    const r = rng ?? globalRNG;

    const eligible = [];
    for (const evt of this._events.values()) {
      const biomeOk = !evt.biomes || evt.biomes.length === 0 || evt.biomes.includes(biome);
      const levelOk =
        (evt.minLevel == null || regionLevel >= evt.minLevel) &&
        (evt.maxLevel == null || regionLevel <= evt.maxLevel);
      if (biomeOk && levelOk) eligible.push(evt);
    }

    if (eligible.length === 0) {
      DebugLog.warn(`[encounter] No eligible random events for biome=${biome} level=${regionLevel}`);
      return null;
    }

    return r.pick(eligible);
  },

  // ── Event resolution ──────────────────────────────────────────────────────

  /**
   * Execute a random event by id, applying the first option's effects automatically
   * for events with a single forced option, or emitting a UI prompt event for
   * events with multiple player choices.
   *
   * Effects handled directly:
   *   - gold (WorldState.partyGold)
   *   - rations (WorldState.rationPoints)
   *   - fame (WorldState.partyFame)
   *   - loot_table (LootSystem.awardLoot)
   *   - combat_enemy (emits encounter:combat)
   *   - skill_xp (emits party:skill_xp)
   *
   * @param {string} eventId
   * @param {object} [party] — WorldState or equivalent; mutated in place for resources
   * @param {ReturnType<import('../utils/RNG.js').createRNG>} [rng]
   * @returns {{ event: object, applied: boolean, message: string }}
   */
  triggerEvent(eventId, party = null, rng = null) {
    const event = this._events.get(eventId);
    if (!event) {
      DebugLog.warn(`[encounter] triggerEvent: unknown event id=${eventId}`);
      return { event: null, applied: false, message: `Unknown event: ${eventId}` };
    }

    this._activeEventId = eventId;
    DebugLog.event(`[encounter] triggerEvent; id=${eventId} title="${event.title}"`);

    const options = event.options ?? [];

    // Events with exactly one forced option are applied immediately.
    if (options.length === 1) {
      this._applyEffect(options[0].effect, party, rng);
      this._activeEventId = null;
      EventBus.emit('event:resolved', { eventId, optionIndex: 0, effect: options[0].effect });
      return { event, applied: true, message: event.description };
    }

    // Multi-option events: emit a prompt so the UI can show choices.
    EventBus.emit('event:prompt', { event, eventId, options });
    return { event, applied: false, message: event.description };
  },

  /**
   * Apply the player's chosen option index for the currently active event.
   * Called by the UI after the player makes a selection.
   *
   * @param {number} optionIndex
   * @param {object} [party]
   * @param {ReturnType<import('../utils/RNG.js').createRNG>} [rng]
   */
  resolveEventOption(optionIndex, party = null, rng = null) {
    const eventId = this._activeEventId;
    if (!eventId) {
      DebugLog.warn('[encounter] resolveEventOption called but no active event');
      return;
    }

    const event = this._events.get(eventId);
    if (!event) return;

    const option = (event.options ?? [])[optionIndex];
    if (!option) {
      DebugLog.warn(`[encounter] resolveEventOption: invalid optionIndex=${optionIndex}`);
      return;
    }

    DebugLog.event(
      `[encounter] Event option chosen; id=${eventId} option="${option.label}"`
    );

    this._applyEffect(option.effect, party, rng);
    this._activeEventId = null;
    EventBus.emit('event:resolved', { eventId, optionIndex, effect: option.effect });
  },

  // ── Internal effect application ───────────────────────────────────────────

  /**
   * Apply a single option effect object.
   * @param {object} effect
   * @param {object|null} party
   * @param {ReturnType<import('../utils/RNG.js').createRNG>} [rng]
   */
  _applyEffect(effect, party, rng = null) {
    if (!effect || typeof effect !== 'object') return;
    const r = rng ?? createRNG(Date.now());

    // Gold
    if (typeof effect.gold === 'number' && party != null) {
      party.partyGold = (party.partyGold ?? 0) + effect.gold;
      DebugLog.info(`[encounter] gold effect; delta=${effect.gold} total=${party.partyGold}`);
    }

    // Rations
    if (typeof effect.rations === 'number' && party != null) {
      party.rationPoints = Math.max(0, (party.rationPoints ?? 0) + effect.rations);
      DebugLog.info(`[encounter] rations effect; delta=${effect.rations} total=${party.rationPoints}`);
    }

    // Fame
    if (typeof effect.fame === 'number' && party != null) {
      party.partyFame = Math.max(-1000, Math.min(1000, (party.partyFame ?? 0) + effect.fame));
      DebugLog.info(`[encounter] fame effect; delta=${effect.fame} total=${party.partyFame}`);
    }

    // Loot table
    if (typeof effect.loot_table === 'string') {
      const partyRef = party ?? (typeof window !== 'undefined' ? window.__worldState : null);
      LootSystem.awardLoot(partyRef, effect.loot_table, r.fork());
    }

    // Combat trigger
    if (typeof effect.combat_enemy === 'string') {
      EventBus.emit('encounter:combat', { enemyId: effect.combat_enemy });
    }

    // Skill XP grant
    if (typeof effect.skill_xp === 'number') {
      EventBus.emit('party:skill_xp', { amount: effect.skill_xp });
    }

    // Skill check
    if (effect.skill_check && typeof effect.skill_check === 'object') {
      this._applySkillCheck(effect.skill_check, party, r);
    }
  },

  /**
   * Resolve a skill check effect and apply the success or failure sub-effect.
   * @param {object} check — { skill, dc, success, failure }
   * @param {object|null} party
   * @param {object} r — RNG instance
   */
  _applySkillCheck(check, party, r) {
    // Simple d20 roll — full skill calculation requires CharacterSystem which
    // is not imported here to keep dependencies lean.
    const roll = r.nextInt(1, 20);
    const dc   = check.dc ?? 10;
    const passed = roll >= dc;

    DebugLog.event(
      `[encounter] skill check; skill=${check.skill} dc=${dc} roll=${roll} passed=${passed}`
    );

    const subEffect = passed ? check.success : check.failure;
    if (subEffect) this._applyEffect(subEffect, party, r);

    EventBus.emit('event:skill_check', { skill: check.skill, dc, roll, passed });
  },
};

// ─── Debug exposure ───────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.__debug = window.__debug ?? {};

  /**
   * Trigger a random event by id for testing.
   * @param {string} eventId
   * @returns {object}
   */
  window.__debug.triggerRandomEvent = function triggerRandomEvent(eventId) {
    const worldState =
      window.__worldState ??
      (typeof WorldState !== 'undefined' ? WorldState : null);
    const result = EventSystem.triggerEvent(eventId, worldState);
    // eslint-disable-next-line no-console
    console.log(`[EventSystem] triggerRandomEvent('${eventId}'):`, result);
    return result;
  };
}

export default EventSystem;
